import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { label: "Home", href: "/" },
    { label: "About", href: "/about" },
    { label: "Course", href: "/course" },
    { label: "Toolkit", href: "/toolkit" },
    { label: "Blog", href: "/blog" },
  ];

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (href.startsWith("/#")) {
      const id = href.replace("/#", "");
      if (location.pathname === "/") {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      } else {
        window.location.href = href;
      }
    }
  };

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50 border-b border-[#E5E0D8]/50"
      style={{
        background: "rgba(250, 249, 245, 0.7)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="header-logo"
          className="font-serif text-xl md:text-2xl font-semibold tracking-tight"
          style={{ color: "var(--ll-text)" }}
        >
          The Lemonade Living Co.
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href.startsWith("/#") ? "/" : link.href}
              data-testid={`nav-${link.label.toLowerCase()}`}
              onClick={() => handleNavClick(link.href)}
              className="text-sm font-medium tracking-wide transition-colors duration-300 hover:opacity-70"
              style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/shop"
            data-testid="nav-shop-cta"
            className="rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5"
            style={{
              background: "var(--ll-cta)",
              color: "var(--ll-cta-text)",
              fontFamily: "'Outfit', sans-serif",
            }}
          >
            Shop
          </Link>
        </nav>

        {/* Mobile Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ color: "var(--ll-text)" }}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-[#E5E0D8]/50 px-6 py-6 space-y-4"
          style={{ background: "rgba(250, 249, 245, 0.95)", backdropFilter: "blur(24px)" }}
        >
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href.startsWith("/#") ? "/" : link.href}
              data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              onClick={() => handleNavClick(link.href)}
              className="block text-base font-medium"
              style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/shop"
            data-testid="mobile-nav-shop-cta"
            onClick={() => setMobileOpen(false)}
            className="block rounded-full px-6 py-3 text-center text-base font-medium mt-2"
            style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}
          >
            Shop
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
